/**
 * book_write — AI Write Command for Book Projects
 *
 * Allows the AI to update an open BookProject via Space Chat:
 *   - Book metadata (title, author, subtitle, genre, …)
 *   - Cover (background image, filters, overlay, text layers)
 *   - Interior format settings (page size, margins, fonts, headers, TOC, …)
 *   - Block styles (bold, italic, color, font-size on individual content blocks)
 *   - Inline images (inserted between paragraphs, reflowed with text)
 *   - Whole-page art (pinned to a specific page, never reflowed)
 *
 * Pattern mirrors canvas_write / slide_write: look up the live
 * BookFormatterApplication via applicationRegistry, then mutate Y.js directly.
 *
 * IMPORTANT: AI cannot edit text content — text is import-locked.
 * The AI can only change styling and add/remove/update images.
 * Always call entity_read with { className: "pages" } first to get blockIds
 * and page numbers before making targeted edits.
 */

import { CommandExecutionContext } from '../../../types/commands';
import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { getApplicationForEntity } from '../../../services/applicationRegistry';
import { generateGrokImage, searchAndDownloadUnsplash } from '../../../services/imageService';
import { BookFormatterApplication, WholePageArt, ContentBlock } from '../services/BookFormatterApplication';
import { CoverLayer, BackgroundLayer, TextLayer, ShapeLayer } from '../types/cover';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

async function resolveImage(imageSource: 'grok' | 'unsplash', prompt?: string, query?: string): Promise<string> {
  if (imageSource === 'grok') {
    if (!prompt) throw new Error('prompt is required when imageSource is "grok"');
    const result = await generateGrokImage(prompt, { width: 1024, height: 1024 });
    return result.url;
  }
  if (!query) throw new Error('query is required when imageSource is "unsplash"');
  const result = await searchAndDownloadUnsplash(query);
  return result.url;
}

// ─────────────────────────────────────────────────────────────────────────────
// Command definition
// ─────────────────────────────────────────────────────────────────────────────

export const bookWriteCommand: SimpleDocCommand = {
  id: 'book_write',
  description: `Update a Book Project — metadata, cover design, interior formatting, block styles, and images.

**SUPPORTED ENTITY:** BookProject (bookproject_*)

**WORKFLOW:** Always call entity_read with { className: "pages" } first to get blockIds and
page numbers. Then use the operations below to make changes.

**OPERATIONS:**

update_metadata — change title, author, subtitle, genre, ISBN, publisher, description, keywords
  { operation: "update_metadata", entityId: "bookproject_red_mountain_blue_river",
    metadata: { title: "New Title", author: "Jane Doe" } }

update_cover — set background image, adjust filters, add/remove overlay, update text layers
  Background image (grok or unsplash):
    { cover: { backgroundImage: { imageSource: "grok", prompt: "dark misty forest" } } }
  Background filters (darken/brighten/blur — 100 = no change; run after setting image or standalone):
    { cover: { backgroundFilters: { brightness: 65, contrast: 110, blur: 1 } } }
    brightness/contrast/saturation: 0–200; blur: 0–20px
  Overlay (semi-transparent color layer over the background — great for making text readable):
    { cover: { overlay: { color: "#000000", opacity: 0.45, blendMode: "normal" } } }
    blendMode: "normal" | "multiply" | "screen" | "overlay" | "darken" | "lighten"
    Remove overlay: { cover: { overlay: null } }
  Text layers (role: "title" | "author"):
    { cover: { layers: [{ role: "title", color: "#fff", position: 20 }, { role: "author", position: 72 }] } }
    position: "top"(y=25) | "center"(y=45) | "bottom"(y=85) | number 0–100 (% from top)
    WARNING: title and author must never share the same position — they will overlap.
    Default layout: title ~28, author ~64. Keep at least 15 units apart.
    Long titles wrap to multiple lines and push text downward — account for this when spacing.
  Combine multiple updates in one call:
    { cover: { backgroundImage: { imageSource: "grok", prompt: "misty forest" },
               backgroundFilters: { brightness: 70 },
               overlay: { color: "#000", opacity: 0.35 },
               layers: [{ role: "title", color: "#fff", position: "bottom" }] } }

update_format — change interior formatting (always triggers repagination)
  Page size values: "5x8", "5.5x8.5", "6x9" (default), "7x10", "8x10"
  { operation: "update_format", entityId: "bookproject_red_mountain_blue_river",
    format: {
      pageSize: "6x9", bodyFont: "Garamond", bodyFontSize: 12, lineHeight: 1.6,
      headingFont: "Garamond", headingFontSize: 18, chapterPageBreak: true,
      margins: { top: 0.75, bottom: 0.75, inside: 0.875, outside: 0.625 },
      header: { enabled: true, content: "{title}", position: "top" },
      footer: { enabled: true, content: "pageNumber", position: "bottom" },
      tableOfContents: { enabled: true, title: "Contents", showPageNumbers: true, dotLeader: "dots" }
    }
  }

update_style — change styling of a specific content block (use entity_read to get blockId). Always triggers repagination.
  { operation: "update_style", entityId: "bookproject_red_mountain_blue_river",
    chapterId: "chapter-abc", blockId: "block-xyz",
    style: { bold: true, color: "#1a1a1a", fontSize: 13 } }
  Style fields: bold (boolean), italic (boolean), color (CSS color), fontSize (number in px)

add_inline_image — insert an image between two paragraphs (reflowed with text, triggers repagination)
  { operation: "add_inline_image", entityId: "bookproject_red_mountain_blue_river",
    chapterId: "chapter-abc", afterBlockId: "block-xyz",
    imageSource: "grok", prompt: "a moonlit ocean at midnight" }
  { operation: "add_inline_image", entityId: "bookproject_red_mountain_blue_river",
    chapterId: "chapter-abc", afterBlockId: null,
    imageSource: "unsplash", query: "moonlit ocean" }
  afterBlockId: blockId of the block after which to insert (null = end of chapter)

remove_inline_image — remove an inline image block (triggers repagination)
  { operation: "remove_inline_image", entityId: "bookproject_red_mountain_blue_river",
    chapterId: "chapter-abc", blockId: "block-xyz" }

add_whole_page_art — insert a full-page image as its own dedicated page AFTER text page N (does NOT reflow text)
  { operation: "add_whole_page_art", entityId: "bookproject_red_mountain_blue_river",
    pinnedPageNumber: 5, imageSource: "grok", prompt: "mountain at dusk" }
  { operation: "add_whole_page_art", entityId: "bookproject_red_mountain_blue_river",
    pinnedPageNumber: 12, imageSource: "unsplash", query: "dramatic landscape" }

update_whole_page_art — replace the image for existing whole-page art (use entity_read to get ID)
  { operation: "update_whole_page_art", entityId: "bookproject_red_mountain_blue_river",
    artId: "art-1234-abc", imageSource: "grok", prompt: "sunrise over mountains" }

remove_whole_page_art — remove a whole-page art entry
  { operation: "remove_whole_page_art", entityId: "bookproject_red_mountain_blue_river",
    artId: "art-1234-abc" }

**COVER TEXT LAYER FIELDS:**
role (required): "title" | "author"
text, font, fontSize, fontWeight, color, align, transform, letterSpacing, lineHeight
shadow: { x, y, blur, color }
position: "top"(25%) | "center"(45%) | "bottom"(85%) | number 0–100 (% from top)
  Never place title and author at the same position — they will overlap.
  Long titles wrap to multiple lines and push content further down — leave space accordingly.
opacity: 0–1  (layer transparency)
fontSize: canvas is 1600×2400px — always use large sizes. Recommended: title 160–240, author 80–120.
  Values like 48 are far too small. Defaults: title=210, author=96.

**FONT OPTIONS FOR INTERIOR:**
bodyFont / headingFont: "Garamond" | "Georgia" | "Times New Roman" | "Palatino" | "Arial" | "Helvetica" | "Lato" | "Raleway"

**TIP:** Use entity_read with { className: "pages" } first to see blockIds before writing.
`,

  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'BookProject entity ID (bookproject_*)',
        required: true,
        validator: (value: string) => {
          if (!value) return 'entityId is required';
          if (!value.startsWith('bookproject_')) return 'entityId must start with "bookproject_"';
          return null;
        }
      },
      {
        name: 'operation',
        type: 'string',
        description: 'Operation: update_metadata | update_cover | update_format | update_style | add_inline_image | remove_inline_image | add_whole_page_art | update_whole_page_art | remove_whole_page_art',
        required: true,
        enum: [
          'update_metadata', 'update_cover', 'update_format',
          'update_style',
          'add_inline_image', 'remove_inline_image',
          'add_whole_page_art', 'update_whole_page_art', 'remove_whole_page_art',
        ],
        validator: (value: string) => {
          const valid = [
            'update_metadata', 'update_cover', 'update_format',
            'update_style',
            'add_inline_image', 'remove_inline_image',
            'add_whole_page_art', 'update_whole_page_art', 'remove_whole_page_art',
          ];
          if (!valid.includes(value)) return `Invalid operation. Must be one of: ${valid.join(', ')}`;
          return null;
        }
      },
      {
        name: 'metadata',
        type: 'object',
        description: 'Metadata fields (for update_metadata): title, author, subtitle, genre, isbn, publisher, description, keywords[]',
        required: false
      },
      {
        name: 'cover',
        type: 'object',
        description: `Cover updates (for update_cover). All fields are optional; include only what you want to change:
  backgroundImage: { imageSource: "grok"|"unsplash", prompt?: string, query?: string }
  backgroundFilters: { brightness?: 0-200, contrast?: 0-200, saturation?: 0-200, blur?: 0-20 }  (100 = no change)
  overlay: { color: string, opacity?: 0-1, blendMode?: "normal"|"multiply"|"screen"|"overlay"|"darken"|"lighten" } | null (null removes it)
  layers: [{ role: "title"|"author", text?, font?, fontSize?, fontWeight?, color?, align?, transform?, letterSpacing?, lineHeight?, shadow?, position?: "top"|"center"|"bottom"|number 0-100, opacity?: 0-1 }]
  (keep title+author at least 15 units apart; use large fontSize — title 160-240, author 80-120)`,
        required: false
      },
      {
        name: 'format',
        type: 'object',
        description: 'Format settings (for update_format): pageSize, bodyFont, bodyFontSize, lineHeight, headingFont, headingFontSize, chapterPageBreak, margins, header, footer, tableOfContents',
        required: false
      },
      {
        name: 'chapterId',
        type: 'string',
        description: 'Chapter ID (for update_style, add_inline_image, remove_inline_image). Use entity_read to get chapter IDs.',
        required: false
      },
      {
        name: 'blockId',
        type: 'string',
        description: 'Block ID (for update_style, remove_inline_image). Use entity_read with { className: "pages" } to get block IDs.',
        required: false
      },
      {
        name: 'style',
        type: 'object',
        description: 'Style to apply (for update_style): bold, italic, color, fontSize',
        required: false,
        validator: (value: any, params: any) => {
          if (params.operation === 'update_style') {
            if (!value) return 'style is required for update_style';
            if (!params.chapterId) return 'chapterId is required for update_style';
            if (!params.blockId) return 'blockId is required for update_style';
          }
          return null;
        }
      },
      {
        name: 'afterBlockId',
        type: 'string',
        description: 'Block ID after which to insert inline image (for add_inline_image). Null = end of chapter.',
        required: false
      },
      {
        name: 'imageSource',
        type: 'string',
        description: 'Image source: "grok" | "unsplash" (for add_inline_image, add_whole_page_art, update_whole_page_art)',
        required: false,
        enum: ['grok', 'unsplash']
      },
      {
        name: 'prompt',
        type: 'string',
        description: 'Grok AI image prompt (required when imageSource is "grok")',
        required: false
      },
      {
        name: 'query',
        type: 'string',
        description: 'Unsplash search query (required when imageSource is "unsplash")',
        required: false
      },
      {
        name: 'pinnedPageNumber',
        type: 'number',
        description: 'Text page number after which to insert the art page (for add_whole_page_art). Use entity_read to see page numbers first.',
        required: false,
        validator: (value: any, params: any) => {
          if (params.operation === 'add_whole_page_art') {
            if (typeof value !== 'number' || value < 1) return 'pinnedPageNumber must be a positive number for add_whole_page_art';
          }
          return null;
        }
      },
      {
        name: 'artId',
        type: 'string',
        description: 'Whole-page art ID (for update_whole_page_art, remove_whole_page_art). Use entity_read to get art IDs.',
        required: false,
        validator: (value: any, params: any) => {
          if (['update_whole_page_art', 'remove_whole_page_art'].includes(params.operation) && !value) {
            return 'artId is required for this operation';
          }
          return null;
        }
      },
    ]
  },

  examples: {
    good: [
      {
        input: { entityId: 'bookproject_red_mountain_blue_river', operation: 'update_metadata', metadata: { title: 'The Forest', author: 'Jane Doe' } },
        expectedOutput: createSuccessResult('Book metadata updated', {}),
        description: 'Update book title and author'
      },
      {
        input: { entityId: 'bookproject_red_mountain_blue_river', operation: 'update_cover', cover: { backgroundImage: { imageSource: 'grok', prompt: 'misty forest at dawn' } } },
        expectedOutput: createSuccessResult('Cover updated with new background image', {}),
        description: 'Generate a new AI cover background'
      },
      {
        input: { entityId: 'bookproject_red_mountain_blue_river', operation: 'add_whole_page_art', pinnedPageNumber: 5, imageSource: 'grok', prompt: 'mountain sunrise' },
        expectedOutput: createSuccessResult('Whole-page art added at page 5', {}),
        description: 'Add a full-page illustration pinned to page 5'
      },
    ],
    bad: [],
  },

  performance: { expectedDuration: 5000, complexity: 'medium' },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      entityId, operation, metadata, cover, format,
      chapterId, blockId, style,
      afterBlockId, imageSource, prompt, query,
      pinnedPageNumber, artId,
    } = params;
    const startTime = Date.now();

    const rawApp = await getApplicationForEntity(entityId);
    if (!rawApp) {
      return createErrorResult(
        'Book not found',
        `No open BookProject found for entityId "${entityId}". The book must be open in a window.`,
        'execution'
      );
    }

    const app = rawApp as BookFormatterApplication;

    try {
      switch (operation) {

        // ─── update_metadata ──────────────────────────────────────────────
        case 'update_metadata': {
          if (!metadata || typeof metadata !== 'object') {
            return createErrorResult('Invalid params', 'metadata object is required for update_metadata', 'validation');
          }
          app.updateMetadata(metadata);

          // Keep cover text layers in sync when title or author changes
          const { title: newTitle, author: newAuthor } = metadata as any;
          if (newTitle !== undefined || newAuthor !== undefined) {
            const currentCover = app.getCover();
            if (currentCover) {
              const updatedLayers = currentCover.layers.map((layer) => {
                if (layer.type !== 'text') return layer;
                const data = layer.data as TextLayer;
                if (data.role === 'title' && newTitle !== undefined) {
                  return { ...layer, data: { ...data, text: newTitle } as TextLayer };
                }
                if (data.role === 'author' && newAuthor !== undefined) {
                  return { ...layer, data: { ...data, text: newAuthor } as TextLayer };
                }
                return layer;
              });
              app.saveCover({
                ...currentCover,
                layers: updatedLayers,
                updatedAt: new Date().toISOString(),
                version: currentCover.version + 1,
              });
            }
          }

          return createSuccessResult(`Book metadata updated`, { updated: Object.keys(metadata) }, { metadata: { executionTime: Date.now() - startTime } as any });
        }

        // ─── update_cover ─────────────────────────────────────────────────
        case 'update_cover': {
          if (!cover || typeof cover !== 'object') {
            return createErrorResult('Invalid params', 'cover object is required for update_cover', 'validation');
          }

          const hasAnyUpdate =
            cover.backgroundImage ||
            cover.backgroundFilters ||
            'overlay' in cover ||
            Array.isArray(cover.layers);
          if (!hasAnyUpdate) {
            return createErrorResult('Invalid params', 'cover must include backgroundImage, backgroundFilters, overlay, or layers', 'validation');
          }

          // Validate backgroundFilters values
          if (cover.backgroundFilters) {
            const bf = cover.backgroundFilters;
            for (const field of ['brightness', 'contrast', 'saturation'] as const) {
              if (bf[field] !== undefined && (typeof bf[field] !== 'number' || bf[field] < 0 || bf[field] > 200)) {
                return createErrorResult('Invalid params', `backgroundFilters.${field} must be a number between 0 and 200`, 'validation');
              }
            }
            if (bf.blur !== undefined && (typeof bf.blur !== 'number' || bf.blur < 0 || bf.blur > 20)) {
              return createErrorResult('Invalid params', 'backgroundFilters.blur must be a number between 0 and 20', 'validation');
            }
          }

          // Validate overlay
          if (cover.overlay !== undefined && cover.overlay !== null) {
            if (typeof cover.overlay.color !== 'string') {
              return createErrorResult('Invalid params', 'overlay.color must be a string (CSS color or hex)', 'validation');
            }
            if (cover.overlay.opacity !== undefined && (typeof cover.overlay.opacity !== 'number' || cover.overlay.opacity < 0 || cover.overlay.opacity > 1)) {
              return createErrorResult('Invalid params', 'overlay.opacity must be a number between 0 and 1', 'validation');
            }
            const validBlendModes = ['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten'];
            if (cover.overlay.blendMode !== undefined && !validBlendModes.includes(cover.overlay.blendMode)) {
              return createErrorResult('Invalid params', `overlay.blendMode must be one of: ${validBlendModes.join(', ')}`, 'validation');
            }
          }

          const currentCover = app.getCover();
          if (!currentCover) {
            return createErrorResult('No cover', 'The book has no cover yet. Open the book first.', 'execution');
          }

          const updatedLayers = [...currentCover.layers];

          // ── Background image ──────────────────────────────────────────────
          if (cover.backgroundImage) {
            const { imageSource: src, prompt: p, query: q } = cover.backgroundImage;
            if (!src || !['grok', 'unsplash'].includes(src)) {
              return createErrorResult('Invalid params', 'cover.backgroundImage.imageSource must be "grok" or "unsplash"', 'validation');
            }
            if (src === 'grok' && !p) return createErrorResult('Invalid params', 'prompt is required when imageSource is "grok"', 'validation');
            if (src === 'unsplash' && !q) return createErrorResult('Invalid params', 'query is required when imageSource is "unsplash"', 'validation');
            const imageUrl = await resolveImage(src, p, q);
            const bgIndex = updatedLayers.findIndex(l => l.type === 'background');
            // Preserve existing filters; only reset to neutral defaults if no filters exist yet
            const existingFilters = bgIndex >= 0
              ? (updatedLayers[bgIndex].data as BackgroundLayer).imageFilters
              : undefined;
            const bgLayer: CoverLayer = {
              id: bgIndex >= 0 ? updatedLayers[bgIndex].id : `layer-bg-${Date.now()}`,
              type: 'background',
              visible: true,
              locked: false,
              opacity: 1,
              blendMode: 'normal',
              position: { x: 0, y: 0, width: 100, height: 100 },
              data: {
                type: 'image',
                imageUrl,
                imageFilters: existingFilters ?? { brightness: 100, contrast: 100, saturation: 100, blur: 0 }
              } as BackgroundLayer
            };
            if (bgIndex >= 0) updatedLayers[bgIndex] = bgLayer;
            else updatedLayers.unshift(bgLayer);
          }

          // ── Background filters (patch only specified fields) ──────────────
          if (cover.backgroundFilters) {
            const bgIdx = updatedLayers.findIndex(l => l.type === 'background');
            if (bgIdx >= 0) {
              const bgData = updatedLayers[bgIdx].data as BackgroundLayer;
              const existing = bgData.imageFilters ?? { brightness: 100, contrast: 100, saturation: 100, blur: 0 };
              updatedLayers[bgIdx] = {
                ...updatedLayers[bgIdx],
                data: { ...bgData, imageFilters: { ...existing, ...cover.backgroundFilters } } as BackgroundLayer
              };
            }
          }

          // ── Overlay (full-bleed shape layer) ─────────────────────────────
          if ('overlay' in cover) {
            const existingIdx = updatedLayers.findIndex(l => l.id.startsWith('layer-overlay'));
            if (cover.overlay === null) {
              if (existingIdx >= 0) updatedLayers.splice(existingIdx, 1);
            } else {
              const { color, opacity = 0.5, blendMode = 'normal' } = cover.overlay;
              const overlayLayer: CoverLayer = {
                id: existingIdx >= 0 ? updatedLayers[existingIdx].id : `layer-overlay-${Date.now()}`,
                type: 'shape',
                visible: true,
                locked: false,
                opacity,
                blendMode: blendMode as CoverLayer['blendMode'],
                position: { x: 0, y: 0, width: 100, height: 100 },
                data: { shapeType: 'rectangle', fill: color } as ShapeLayer,
              };
              if (existingIdx >= 0) {
                updatedLayers[existingIdx] = overlayLayer;
              } else {
                // Insert after background, before text layers
                const bgIdx = updatedLayers.findIndex(l => l.type === 'background');
                updatedLayers.splice(bgIdx + 1, 0, overlayLayer);
              }
            }
          }

          // ── Text layers ───────────────────────────────────────────────────
          if (Array.isArray(cover.layers)) {
            for (const layerUpdate of cover.layers) {
              const { role, position: pos, opacity: layerOpacity, ...textProps } = layerUpdate;
              const idx = updatedLayers.findIndex(l => l.type === 'text' && (l.data as TextLayer).role === role);
              if (idx >= 0) {
                const existingLayer = updatedLayers[idx];
                const existingData = existingLayer.data as TextLayer;

                // Validate position if provided
                if (pos !== undefined) {
                  const isShorthand = ['top', 'center', 'bottom'].includes(pos);
                  const isNumeric = typeof pos === 'number' && pos >= 0 && pos <= 100;
                  if (!isShorthand && !isNumeric) {
                    return createErrorResult('Invalid params',
                      `layers[].position must be "top", "center", "bottom", or a number 0–100 (% from top)`, 'validation');
                  }
                }
                // Validate opacity if provided
                if (layerOpacity !== undefined && (typeof layerOpacity !== 'number' || layerOpacity < 0 || layerOpacity > 1)) {
                  return createErrorResult('Invalid params', 'layers[].opacity must be a number between 0 and 1', 'validation');
                }

                const resolvedY = pos === 'top' ? 25 : pos === 'center' ? 45 : pos === 'bottom' ? 85 : (pos as number);

                updatedLayers[idx] = {
                  ...existingLayer,
                  opacity: layerOpacity !== undefined ? layerOpacity : existingLayer.opacity,
                  position: pos !== undefined
                    ? { ...existingLayer.position, y: resolvedY }
                    : existingLayer.position,
                  data: { ...existingData, ...textProps } as TextLayer,
                };
              }
            }
          }

          const changedParts: string[] = [];
          if (cover.backgroundImage) changedParts.push('background image');
          if (cover.backgroundFilters) changedParts.push('background filters');
          if ('overlay' in cover) changedParts.push(cover.overlay === null ? 'overlay removed' : 'overlay');
          if (Array.isArray(cover.layers)) changedParts.push('text layers');

          const newCover = { ...currentCover, layers: updatedLayers, updatedAt: new Date().toISOString(), version: currentCover.version + 1 };
          app.saveCover(newCover);
          return createSuccessResult(
            `Cover updated: ${changedParts.join(', ')}`,
            { coverId: newCover.id, version: newCover.version, updated: changedParts },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        // ─── update_format ────────────────────────────────────────────────
        case 'update_format': {
          if (!format || typeof format !== 'object') {
            return createErrorResult('Invalid params', 'format object is required for update_format', 'validation');
          }
          app.updateFormatSettings(format);
          // Format changes always require repagination
          app.triggerRepagination().catch(err =>
            console.warn('[book_write] Repagination after format update failed:', err)
          );
          return createSuccessResult(
            `Format settings updated`,
            { updated: Object.keys(format) },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        // ─── update_style ─────────────────────────────────────────────────
        case 'update_style': {
          app.updateBlockStyle(chapterId, blockId, style);
          // Always repaginate: BookPageRenderer reads from computedPages (yPages),
          // which is only updated via the backend repagination round-trip.
          app.triggerRepagination().catch(err =>
            console.warn('[book_write] Repagination after style update failed:', err)
          );
          return createSuccessResult(
            `Block style updated`,
            { chapterId, blockId, style },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        // ─── add_inline_image ─────────────────────────────────────────────
        case 'add_inline_image': {
          if (!chapterId) return createErrorResult('Invalid params', 'chapterId is required for add_inline_image', 'validation');
          if (!imageSource || !['grok', 'unsplash'].includes(imageSource)) {
            return createErrorResult('Invalid params', 'imageSource must be "grok" or "unsplash"', 'validation');
          }
          if (imageSource === 'grok' && !prompt) return createErrorResult('Invalid params', 'prompt is required when imageSource is "grok"', 'validation');
          if (imageSource === 'unsplash' && !query) return createErrorResult('Invalid params', 'query is required when imageSource is "unsplash"', 'validation');

          const imageUrl = await resolveImage(imageSource, prompt, query);
          const block: ContentBlock = {
            id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            type: 'inline_image',
            imageUrl,
            imageSource,
            imagePrompt: imageSource === 'grok' ? prompt : undefined,
            imageQuery: imageSource === 'unsplash' ? query : undefined,
          };

          app.addInlineImage(chapterId, afterBlockId ?? null, block);
          return createSuccessResult(
            `Inline image added to chapter`,
            { chapterId, blockId: block.id, afterBlockId: afterBlockId ?? null, imageUrl },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        // ─── remove_inline_image ──────────────────────────────────────────
        case 'remove_inline_image': {
          if (!chapterId) return createErrorResult('Invalid params', 'chapterId is required for remove_inline_image', 'validation');
          if (!blockId) return createErrorResult('Invalid params', 'blockId is required for remove_inline_image', 'validation');
          app.removeInlineImage(chapterId, blockId);
          return createSuccessResult(
            `Inline image removed`,
            { chapterId, blockId },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        // ─── add_whole_page_art ───────────────────────────────────────────
        case 'add_whole_page_art': {
          if (typeof pinnedPageNumber !== 'number' || pinnedPageNumber < 1) {
            return createErrorResult('Invalid params', 'pinnedPageNumber must be a positive number', 'validation');
          }
          if (!imageSource || !['grok', 'unsplash'].includes(imageSource)) {
            return createErrorResult('Invalid params', 'imageSource must be "grok" or "unsplash"', 'validation');
          }
          if (imageSource === 'grok' && !prompt) return createErrorResult('Invalid params', 'prompt is required when imageSource is "grok"', 'validation');
          if (imageSource === 'unsplash' && !query) return createErrorResult('Invalid params', 'query is required when imageSource is "unsplash"', 'validation');

          const imageUrl = await resolveImage(imageSource, prompt, query);
          const art: WholePageArt = {
            id: `art-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            pinnedPageNumber,
            imageUrl,
            imageSource,
            imagePrompt: imageSource === 'grok' ? prompt : undefined,
            imageQuery: imageSource === 'unsplash' ? query : undefined,
          };

          app.addWholePageArt(art);
          return createSuccessResult(
            `Whole-page art added at page ${pinnedPageNumber}`,
            { artId: art.id, pinnedPageNumber, imageUrl },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        // ─── update_whole_page_art ────────────────────────────────────────
        case 'update_whole_page_art': {
          if (!artId) return createErrorResult('Invalid params', 'artId is required for update_whole_page_art', 'validation');
          if (!imageSource || !['grok', 'unsplash'].includes(imageSource)) {
            return createErrorResult('Invalid params', 'imageSource must be "grok" or "unsplash"', 'validation');
          }
          if (imageSource === 'grok' && !prompt) return createErrorResult('Invalid params', 'prompt is required when imageSource is "grok"', 'validation');
          if (imageSource === 'unsplash' && !query) return createErrorResult('Invalid params', 'query is required when imageSource is "unsplash"', 'validation');

          const imageUrl = await resolveImage(imageSource, prompt, query);
          app.updateWholePageArt(artId, {
            imageUrl,
            imageSource,
            imagePrompt: imageSource === 'grok' ? prompt : undefined,
            imageQuery: imageSource === 'unsplash' ? query : undefined,
          });
          return createSuccessResult(
            `Whole-page art ${artId} updated`,
            { artId, imageUrl },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        // ─── remove_whole_page_art ────────────────────────────────────────
        case 'remove_whole_page_art': {
          if (!artId) return createErrorResult('Invalid params', 'artId is required for remove_whole_page_art', 'validation');
          app.removeWholePageArt(artId);
          return createSuccessResult(
            `Whole-page art ${artId} removed`,
            { artId },
            { metadata: { executionTime: Date.now() - startTime } as any }
          );
        }

        default:
          return createErrorResult('Unknown operation', `Operation "${operation}" is not supported.`, 'validation');
      }
    } catch (err: any) {
      console.error('[book_write] Handler error:', err);
      return createErrorResult(
        'book_write failed',
        err?.message || 'An unexpected error occurred',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } as any }
      );
    }
  }
};
